import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  CheckCircle2,
  KeyRound,
  MoreHorizontal,
  Percent,
  Plus,
  RotateCw,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import type { CredentialRow } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { DataTable } from '../../../../components/data/DataTable';
import { EmptyState } from '../../../../components/data/EmptyState';
import { CredentialFormDialog } from '../../../../components/credentials/CredentialFormDialog';
import { MultiplierDialog } from '../../../../components/credentials/MultiplierDialog';
import { RotateDialog } from '../../../../components/credentials/RotateDialog';
import { toast } from '../../../../components/ui/sonner';
import { ApiError } from '../../../../lib/auth/client';
import {
  credentialsKey,
  deleteCredential,
  listCredentials,
  testCredential,
} from '../../../../lib/queries/credentials';

function CredentialsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [rotating, setRotating] = useState<CredentialRow | null>(null);
  const [editingMultiplier, setEditingMultiplier] = useState<CredentialRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CredentialRow | null>(null);

  const query = { page, limit: 25 };
  const credentials = useQuery({
    queryKey: credentialsKey(query),
    queryFn: () => listCredentials(query),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCredential(id),
    onSuccess: () => {
      toast.success('Kimlik bilgisi silindi');
      qc.invalidateQueries({ queryKey: ['credentials'] });
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => testCredential(id),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['credentials'] });
      if (resp.ok) toast.success(`Bağlantı başarılı (${resp.providerStatus ?? 200})`);
      else toast.error(`Bağlantı başarısız: ${resp.message ?? resp.providerStatus ?? '-'}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Test başarısız');
    },
  });

  const columns = useMemo<ColumnDef<CredentialRow, unknown>[]>(
    () => [
      { accessorKey: 'customerName', header: 'Müşteri' },
      {
        accessorKey: 'providerSlug',
        header: 'Sağlayıcı',
        cell: ({ row }) => <Badge variant="secondary">{row.original.providerName}</Badge>,
      },
      { accessorKey: 'label', header: 'Etiket' },
      {
        accessorKey: 'identifier',
        header: 'Tanımlayıcı',
        cell: ({ row }) => (
          <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">
            {row.original.identifier}
          </code>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Son Kullanım',
        cell: ({ row }) =>
          row.original.lastUsedAt
            ? formatDistanceToNow(new Date(row.original.lastUsedAt), { addSuffix: true, locale: tr })
            : '—',
      },
      {
        accessorKey: 'usageMultiplier',
        header: 'Katsayı',
        cell: ({ row }) => `×${Number(row.original.usageMultiplier).toFixed(2)}`,
      },
      {
        accessorKey: 'lastUsedStatus',
        header: 'Durum',
        cell: ({ row }) => {
          const s = row.original.lastUsedStatus;
          if (s === 'success')
            return (
              <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Başarılı
              </Badge>
            );
          if (s === 'failed')
            return (
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                Hata
              </Badge>
            );
          return <span className="text-slate-400 text-xs">—</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const c = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => test.mutate(c.id)} disabled={test.isPending}>
                  <Zap className="h-4 w-4" /> Test Et
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setRotating(c)}>
                  <RotateCw className="h-4 w-4" /> Rotate
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setEditingMultiplier(c)}>
                  <Percent className="h-4 w-4" /> Katsayı Güncelle
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                  onSelect={() => setPendingDelete(c)}
                >
                  <Trash2 className="h-4 w-4" /> Sil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [test],
  );

  return (
    <>
      <PageHeader
        title="Kimlik Bilgileri"
        description="Sağlayıcı API anahtar yönetimi"
        actions={
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4" /> Yeni Kimlik Bilgisi
          </Button>
        }
      />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={credentials.data?.data}
          loading={credentials.isLoading}
          getRowId={(row) => row.id}
          empty={
            <EmptyState
              icon={KeyRound}
              title="Henüz kimlik bilgisi yok"
              description="İlk API anahtarını ekleyerek başlayın."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="h-4 w-4" /> Kimlik Bilgisi Ekle
                </Button>
              }
            />
          }
          pagination={
            credentials.data
              ? {
                  page: credentials.data.page,
                  limit: credentials.data.limit,
                  total: credentials.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <CredentialFormDialog open={formOpen} onOpenChange={setFormOpen} />

      {rotating && (
        <RotateDialog
          open
          onOpenChange={(o) => !o && setRotating(null)}
          credentialId={rotating.id}
          label={`${rotating.customerName} — ${rotating.label}`}
          providerSlug={rotating.providerSlug}
        />
      )}

      {editingMultiplier && (
        <MultiplierDialog
          open
          onOpenChange={(o) => !o && setEditingMultiplier(null)}
          credentialId={editingMultiplier.id}
          label={`${editingMultiplier.customerName} — ${editingMultiplier.label}`}
          currentMultiplier={editingMultiplier.usageMultiplier}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kimlik bilgisi silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>
                {pendingDelete?.customerName} — {pendingDelete?.label}
              </strong>{' '}
              kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const Route = createFileRoute('/_app/admin/credentials/')({ component: CredentialsPage });
